/**
 * @fileoverview Renderer for the render_ui tool.
 * Renders a json-render spec in the chat with action bridging
 * to the InteractiveChatContext for sending messages.
 */

'use client'

import { useCallback, useMemo, useState, type ReactNode } from 'react'
import {
  Renderer,
  StateProvider,
  ActionProvider,
  VisibilityProvider,
  ValidationProvider,
  useStateStore,
} from '@json-render/react'
import { registry, ChatRendererProvider } from '@/lib/json-render'
import { FormAutoCard, deriveFormTitle } from '@/lib/json-render/form-auto-card'
import { useInteractiveChat } from '@/components/chat/interactive/interactive-context'
import { BubbleFormOverlay } from '@/components/chat/bubble/bubble-form-overlay'
import { FormOpenedPlaceholder } from '@/components/chat/bubble/form-opened-placeholder'
import { isBubbleMode } from '@/utils/bubble-mode'

/** Submit-button params of a render_ui form, or null when the spec isn't a form. */
function findSubmitForm(
  spec: { elements?: Record<string, unknown> } | undefined,
): { formId: string; required: string[]; summary: string } | null {
  for (const raw of Object.values(spec?.elements ?? {})) {
    const press = (raw as { on?: Record<string, { action?: string; params?: Record<string, unknown> }> })?.on?.press
    if (press?.action === 'submitFormData') {
      const p = press.params ?? {}
      return {
        formId: (p.formId as string) || 'form',
        required: Array.isArray(p.required) ? (p.required as string[]) : [],
        summary: (p.summary as string) || '',
      }
    }
  }
  return null
}

/** A decoded json-render spec, or undefined when it cannot be used. */
type DecodedSpec = { root: string; elements: Record<string, unknown> } | undefined

interface SpecRendererProps {
  input: {
    /**
     * `render_ui` takes its spec as JSON TEXT (see the tool definition), so the
     * transcript part normally carries a string. Objects are still accepted so
     * specs recorded before that contract keep rendering.
     */
    spec: { root: string; elements: Record<string, unknown> } | string
  }
  isLastMessage: boolean
  submittedText?: string
  sandboxId?: string
}

/**
 * Decodes the spec into the object the renderer works with.
 *
 * An undecodable spec yields undefined so the caller renders nothing — the tool
 * has already returned a `hint` telling the model what to fix, so failing quietly
 * here is correct.
 *
 * @param spec - Raw spec from the tool call, as text or object
 * @returns The decoded spec, or undefined when unusable
 */
function decodeSpec(spec: SpecRendererProps['input']['spec']): DecodedSpec {
  if (typeof spec !== 'string') return spec
  try {
    const parsed = JSON.parse(spec) as DecodedSpec
    return parsed && typeof parsed === 'object' ? parsed : undefined
  } catch {
    return undefined
  }
}

/** Form element types whose values should be bound to the state store */
const FORM_TYPES = new Set(['Input', 'Textarea', 'Select', 'Radio', 'Checkbox', 'Switch'])

/**
 * Which prop carries a component's value. Checkbox and Switch are boolean
 * controls that read `checked`, not `value` — binding `value` on them lands on
 * a prop they ignore, so their state never reaches the store (a required
 * consent checkbox then blocks submit forever).
 */
function valuePropFor(type: string): 'checked' | 'value' {
  return type === 'Checkbox' || type === 'Switch' ? 'checked' : 'value'
}

/**
 * Auto-adds $bindState bindings to form elements that have a `name` prop
 * but no explicit value binding. This routes form values through the
 * json-render state store so `${/name}` template expressions and
 * `submitFormData` see actual field values.
 *
 * A literal value the model supplied (e.g. a prefilled phone number) would be
 * destroyed by the binding it needs, so it is lifted out into `initialState`
 * and seeded into the store instead — the field still renders prefilled AND
 * the value is present at submit.
 *
 * @returns The bound spec plus the initial state derived from literal props
 */
function addFormBindings(spec: { root: string; elements: Record<string, unknown> }) {
  const elements = { ...spec.elements }
  const initialState: Record<string, unknown> = {}
  let changed = false

  for (const [key, raw] of Object.entries(elements)) {
    const element = raw as { type?: string; props?: Record<string, unknown> }
    if (!element.type || !element.props?.name || !FORM_TYPES.has(element.type)) continue

    const props = element.props as Record<string, unknown>
    const valueProp = valuePropFor(element.type)
    const existing = props[valueProp] as Record<string, unknown> | undefined

    // Already explicitly bound by the author — leave it alone.
    if (existing && typeof existing === 'object' && '$bindState' in existing) continue

    // Preserve a model-supplied literal by seeding the store with it.
    if (existing !== undefined && existing !== null && typeof existing !== 'object') {
      initialState[String(props.name)] = existing
    }

    elements[key] = {
      ...element,
      props: { ...props, [valueProp]: { $bindState: `/${props.name}` } },
    }
    changed = true
  }

  return {
    spec: changed ? { ...spec, elements } : spec,
    initialState,
  }
}

/**
 * Auto-adds on.press bindings to Button elements that lack one.
 * Without an on.press binding, the shadcn Button's emit("press") call
 * silently does nothing. This adds a default sendMessage action that
 * sends the button's label text as a user message.
 */
function addButtonBindings(spec: { root: string; elements: Record<string, unknown> }) {
  const elements = { ...spec.elements }
  let changed = false

  for (const [key, raw] of Object.entries(elements)) {
    const element = raw as { type?: string; props?: Record<string, unknown>; on?: Record<string, unknown> }
    if (element.type !== 'Button') continue
    if (element.on?.press) continue

    const label = (element.props?.label as string) || 'Submit'

    elements[key] = {
      ...element,
      on: {
        ...element.on,
        press: {
          action: 'sendMessage',
          params: { text: label },
        },
      },
    }
    changed = true
  }

  return changed ? { ...spec, elements } : spec
}

/**
 * Renders a json-render spec from the render_ui tool.
 * Bridges json-render actions to the interactive chat context.
 */
export function SpecRenderer({ input, isLastMessage, submittedText, sandboxId }: SpecRendererProps) {
  const { sendMessage } = useInteractiveChat()

  const decoded = useMemo(() => decodeSpec(input?.spec), [input?.spec])

  // A form (a spec whose submit button fires `submitFormData`) is rendered in
  // the polished multi-step card design instead of the generic json-render
  // layout — regardless of how the model arranged the spec.
  const formSubmit = useMemo(() => findSubmitForm(decoded), [decoded])

  const { spec, initialState } = useMemo(() => {
    if (!decoded?.elements) return { spec: decoded, initialState: {} }
    const bound = addFormBindings(decoded)
    return { spec: addButtonBindings(bound.spec), initialState: bound.initialState }
  }, [decoded])

  if (!spec?.root || !spec?.elements) {
    return null
  }

  if (formSubmit && decoded) {
    return (
      <FormAutoBridge
        spec={decoded}
        formSubmit={formSubmit}
        sendMessage={sendMessage}
        isLastMessage={isLastMessage}
      />
    )
  }

  return (
    <ChatRendererProvider isLastMessage={isLastMessage} submittedText={submittedText} sandboxId={sandboxId} sendMessage={sendMessage}>
      <StateProvider initialState={initialState}>
        <VisibilityProvider>
          <ValidationProvider>
            <SpecActionBridge sendMessage={sendMessage} isLastMessage={isLastMessage}>
              <Renderer spec={spec} registry={registry} />
            </SpecActionBridge>
          </ValidationProvider>
        </VisibilityProvider>
      </StateProvider>
    </ChatRendererProvider>
  )
}

/**
 * Bridges json-render actions to the chat context.
 * Lives inside StateProvider so it can resolve ${/path} template
 * expressions in action params using the form state.
 * Tracks submission state to prevent duplicate sends.
 */
function SpecActionBridge({ sendMessage, isLastMessage, children }: { sendMessage: (text: string) => void; isLastMessage: boolean; children: ReactNode }) {
  const { get, getSnapshot } = useStateStore()
  const [submitted, setSubmitted] = useState(false)
  const [missingFields, setMissingFields] = useState<string[]>([])

  /** Form is disabled after submission or when the conversation has moved past this message */
  const isDisabled = submitted || !isLastMessage

  const handleSendMessage = useCallback(async (params: Record<string, unknown>) => {
    if (isDisabled) return

    const rawText = params?.text as string
    if (!rawText) return

    // Interpolate ${/path} template expressions with form state values
    const text = rawText.replace(/\$\{([^}]+)\}/g, (_, path: string) => {
      const value = get(path.trim())
      return String(value ?? '')
    })

    setSubmitted(true)
    sendMessage(text)
  }, [sendMessage, get, isDisabled])

  /**
   * Structured form submit. Reads the whole bound form state (every named input
   * is routed into the store by `addFormBindings`), gates on the declared
   * required fields, and returns KEYED VALUES to the agent instead of the prose
   * that `sendMessage` interpolation produces.
   *
   * `render_ui` is not a `clientSide` tool, so its call has already resolved —
   * the submission is necessarily a new user turn. It travels as a fenced
   * `form-submission` envelope over the existing text channel and is rendered
   * in the transcript as a compact chip (see `isFormSubmissionText`).
   */
  const handleSubmitFormData = useCallback(async (params: Record<string, unknown>) => {
    if (isDisabled) return

    const formId = (params?.formId as string) || 'form'
    const required = Array.isArray(params?.required) ? (params.required as string[]) : []
    const summary = (params?.summary as string) || ''

    // Live snapshot, not the React render snapshot — the last keystroke counts.
    const state = (getSnapshot() ?? {}) as Record<string, unknown>

    const isEmpty = (v: unknown) =>
      v === undefined ||
      v === null ||
      v === false ||
      (typeof v === 'string' && v.trim().length === 0) ||
      (Array.isArray(v) && v.length === 0)

    const missing = required.filter((key) => isEmpty(state[key]))
    if (missing.length > 0) {
      setMissingFields(missing)
      return
    }

    setMissingFields([])
    setSubmitted(true)
    sendMessage(
      [
        '```form-submission',
        JSON.stringify({ formId, summary: summary || undefined, values: state }, null, 2),
        '```',
      ].join('\n'),
    )
  }, [sendMessage, getSnapshot, isDisabled])

  const actionHandlers = useMemo(() => ({
    sendMessage: handleSendMessage,
    submitFormData: handleSubmitFormData,
  }), [handleSendMessage, handleSubmitFormData])

  return (
    <ActionProvider handlers={actionHandlers}>
      <div className={isDisabled ? 'opacity-60 pointer-events-none' : undefined}>
        {children}
      </div>
      {missingFields.length > 0 && (
        <p className="mt-2 text-xs text-destructive" role="alert" dir="auto">
          Please complete: {missingFields.join(', ')}
        </p>
      )}
    </ActionProvider>
  )
}

/**
 * Renders a render_ui form in the polished multi-step card and emits the keyed
 * values as a `form-submission` envelope on submit (same channel as the generic
 * `submitFormData` handler, but the card owns its own field state).
 */
function FormAutoBridge({
  spec,
  formSubmit,
  sendMessage,
  isLastMessage,
}: {
  spec: { root: string; elements: Record<string, unknown> }
  formSubmit: { formId: string; required: string[]; summary: string }
  sendMessage: (text: string) => void
  isLastMessage: boolean
}) {
  const [submitted, setSubmitted] = useState(false)
  const disabled = submitted || !isLastMessage

  /**
   * The visitor closed the sheet. Local and deliberately NOT reported to the
   * agent: the `form-submission` envelope is the only channel back, and a
   * "dismissed" message would be a user turn the user never wrote. Closing just
   * returns the form to the transcript, still fillable.
   */
  const [closed, setClosed] = useState(false)

  const handleSubmit = useCallback(
    (values: Record<string, unknown>) => {
      if (disabled) return
      setSubmitted(true)
      sendMessage(
        [
          '```form-submission',
          JSON.stringify(
            { formId: formSubmit.formId, summary: formSubmit.summary || undefined, values },
            null,
            2,
          ),
          '```',
        ].join('\n'),
      )
    },
    [disabled, sendMessage, formSubmit],
  )

  const card = (layout: 'inline' | 'panel') => (
    <FormAutoCard
      spec={spec}
      formId={formSubmit.formId}
      required={formSubmit.required}
      onSubmit={handleSubmit}
      disabled={disabled}
      layout={layout}
    />
  )

  /**
   * Bubble embed only: the live form is lifted out of the transcript
   * into a sheet that covers the panel, so it cannot scroll away and the
   * composer cannot be typed into past it. Every other surface — main chat, the
   * in-app widget, the builder preview — falls through to the inline card
   * below, unchanged.
   *
   * `isLastMessage` keeps a superseded form from re-opening, and `submitted`
   * closes the sheet on its own once the envelope is on its way.
   */
  if (isBubbleMode()) {
    if (!isLastMessage || submitted) {
      // A form that has been answered, or that the conversation has moved past,
      // is not re-rendered in the widget. Inline it would come back as an EMPTY
      // step-1 card (the sheet's instance is gone), which reads like a second
      // form to fill; the submission chip already records what was sent.
      return null
    }
    const title = deriveFormTitle(spec, formSubmit.formId)
    return (
      <>
        <FormOpenedPlaceholder
          title={title}
          isOpen={!closed}
          onReopen={() => setClosed(false)}
        />
        {/* Rendered even while closed — the sheet hides itself rather than
            unmounting, so a visitor who closes it mid-wizard doesn't lose what
            they already filled in. */}
        <BubbleFormOverlay title={title} open={!closed} onClose={() => setClosed(true)}>
          {card('panel')}
        </BubbleFormOverlay>
      </>
    )
  }

  return card('inline')
}
